import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Coffee, Truck, Star, Fire, Package, ShieldCheck, Medal } from '@phosphor-icons/react';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import ProductCard from '../components/ProductCard';
import AuthModal from '../components/AuthModal';
import { setPageSEO } from '../lib/seo';
import { LANDSCAPE_CAROUSEL } from '../lib/capeEmberAssets';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const HOME_ASSETS = {
  fynbos: '/assets/cape-ember/Fynbos%20Home.PNG',
  gardenRoute: '/assets/cape-ember/Garden%20Route%20Home.PNG',
  emberReserve: '/assets/cape-ember/Ember%20Reserve%20home.PNG',
  karooHorizon: '/assets/cape-ember/Karoo%20Horizon%20Home.PNG',
  landscapeBundle: '/assets/cape-ember/Landscape%20Bundle%20Home.PNG',
};

const HERO_IMAGES = LANDSCAPE_CAROUSEL.map(({ src, alt }) => ({ url: src, alt }));

const STORY_IMAGE = {
  url: HOME_ASSETS.landscapeBundle,
  alt: 'Landscape Bundle lifestyle scene',
  shopPath: '/products/landscape-bundle',
};

const HOME_PRODUCT_IMAGES = {
  'fynbos-roast': HOME_ASSETS.fynbos,
  'garden-route': HOME_ASSETS.gardenRoute,
  'garden-route-blend': HOME_ASSETS.gardenRoute,
  'garden-route-collection': '/assets/cape-ember/cape-ember-four-product-collection.jpeg',
  'sedgefield-mosaic-roast': '/assets/WhatsApp%20Image%202026-10-17%20at%2011.41.17.jpeg',
  'ember-reserve': HOME_ASSETS.emberReserve,
  'karoo-horizon': HOME_ASSETS.karooHorizon,
};

const GOOGLE_REVIEWS = [
  {
    author: 'Rikki Thomas',
    quote: 'Amazing Coffee!!! Package delivered ahead of time. Service was amazing. Avishaan was brilliant to deal with. Coffee beans grind beautifully and the taste was simply phenomenal!!! Will most DEFINITELY order again once my supply runs out. Keep up the spectacular work Cape Ember Team',
    url: 'https://maps.app.goo.gl/u3sBp6k5P69CUM2W8',
  },
  {
    author: 'Tyran Bester',
    quote: "Cape Ember coffee is delicious. I'm a huge fan of coffee and Cape Ember never disappoints. The aroma, smoothe flavors and carefully crafted blends work so well together. Love it and will be ordering more.",
    url: 'https://maps.app.goo.gl/WHWoNXbYUN3XAB6g8',
  },
  {
    author: 'Natalie Klerck',
    quote: 'Every cup of freshly brewed coffee is an experience only Cape Ember beans can provide Beatifuly packadges and options \u2615\uFE0F fast and easy delivery \u{1F44C}',
    url: 'https://maps.app.goo.gl/m7PjL3TEyJnUW6Gn9',
  },
  {
    author: 'Lizelle Britz',
    quote: "We’ve really enjoyed the Garden Route Blend from Cape Ember Beans. Full of flavour, smooth and genuinely delicious. Whether made in a plunger or barista-style in our coffee machine, it tastes great every time. A lovely blend we’ll definitely keep enjoying!",
    url: 'https://maps.app.goo.gl/MFW3fTWznxhsaqxF8',
  },
  {
    author: 'Jeanne-Marie Eksteen',
    quote: 'Absolutely love this coffee brew!',
    url: 'https://maps.app.goo.gl/JpgGrRsGrwjGXEBt7',
  },
];

// Animation variants
const fadeInUp = {
  hidden: { opacity: 0, y: 40 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.8, ease: [0.4, 0, 0.2, 1] } }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.15 } }
};

const HomePage = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [authModalOpen, setAuthModalOpen] = useState(false);
  const [heroImageIndex, setHeroImageIndex] = useState(0);
  const [testimonials, setTestimonials] = useState([]);

  // SEO metadata
  useEffect(() => {
    setPageSEO({
      title: 'Cape Ember Coffee Co. | Premium South African Coffee',
      description: 'Premium coffee inspired by the places we love. Crafted with trusted roasting partners and delivered nationwide in South Africa.',
      canonicalPath: '/',
      image: HOME_ASSETS.fynbos
    });

    const preload = document.createElement('link');
    preload.rel = 'preload';
    preload.as = 'image';
    preload.href = HERO_IMAGES[0].url;
    document.head.appendChild(preload);

    return () => {
      document.head.removeChild(preload);
    };
  }, []);

  // Auto-rotate hero slideshow every 6 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      setHeroImageIndex((prev) => (prev + 1) % HERO_IMAGES.length);
    }, 6000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const response = await axios.get(`${API}/products`);
        const productsData = response.data.products || response.data;
        setProducts(Array.isArray(productsData) ? productsData : []);
      } catch (error) {
        console.error('Failed to fetch products:', error);
        setProducts([]);
      } finally {
        setLoading(false);
      }
    };
    fetchProducts();
  }, []);

  useEffect(() => {
    const fetchReviews = async () => {
      try {
        const response = await axios.get(`${API}/reviews/public?limit=6`);
        setTestimonials(Array.isArray(response.data?.reviews) ? response.data.reviews : []);
      } catch (error) {
        setTestimonials([]);
      }
    };
    fetchReviews();
  }, []);

  const coreIds = ['fynbos-roast', 'garden-route', 'sedgefield-mosaic-roast', 'karoo-horizon', 'ember-reserve'];
  const individualProducts = products
    .filter((p) => coreIds.includes(p.id))
    .sort((a, b) => coreIds.indexOf(a.id) - coreIds.indexOf(b.id));
  const homepageCardProducts = individualProducts.map((product) => ({
    ...product,
    image_url: HOME_PRODUCT_IMAGES[product.id] || HOME_PRODUCT_IMAGES[product.slug] || product.image_url,
  }));
  const features = [
    { icon: Truck, title: 'Nationwide Delivery', desc: 'Reliable courier delivery across South Africa' },
    { icon: ShieldCheck, title: 'Secure Checkout', desc: 'Safe encrypted payments with trusted gateways' },
    { icon: Fire, title: 'Small-Batch Roasted', desc: 'Crafted with trusted roasting partners' },
    { icon: Coffee, title: 'Whole Bean or Ground', desc: 'Choose your grind option at checkout' },
    { icon: Package, title: 'Freshly Packed', desc: 'Packed for freshness and shipped quickly' },
  ];

  const customerReviewStats = {
    rating: testimonials.length ? (testimonials.reduce((sum, t) => sum + Number(t.rating || 0), 0) / testimonials.length) : 0,
    totalReviews: testimonials.length,
    distribution: [
      { stars: 5, percentage: testimonials.length ? Math.round((testimonials.filter(t => Number(t.rating) === 5).length / testimonials.length) * 100) : 0 },
      { stars: 4, percentage: testimonials.length ? Math.round((testimonials.filter(t => Number(t.rating) === 4).length / testimonials.length) * 100) : 0 },
      { stars: 3, percentage: testimonials.length ? Math.round((testimonials.filter(t => Number(t.rating) === 3).length / testimonials.length) * 100) : 0 },
      { stars: 2, percentage: testimonials.length ? Math.round((testimonials.filter(t => Number(t.rating) === 2).length / testimonials.length) * 100) : 0 },
      { stars: 1, percentage: testimonials.length ? Math.round((testimonials.filter(t => Number(t.rating) === 1).length / testimonials.length) * 100) : 0 },
    ]
  };

  const brewingMethods = [
      { name: 'French Press', icon: '☕', time: '4 min' },
      { name: 'Pour Over', icon: '💧', time: '3 min' },
      { name: 'Espresso', icon: '⚡', time: '25 sec' },
      { name: 'Cold Brew', icon: '❄️', time: '12-24 hrs' },
      { name: 'AeroPress', icon: '🔄', time: '1.5 min' },
      { name: 'Moka Pot', icon: '🔥', time: '5 min' },
    ];

    return (
      <div className="min-h-screen bg-[#120d0b] text-[#f5eee7]">
        <section className="relative overflow-hidden bg-[radial-gradient(circle_at_top,_rgba(209,123,69,0.14),_transparent_30%),linear-gradient(180deg,#120d0b_0%,#171210_100%)]" data-testid="hero-section">
          <div className="mx-auto grid max-w-7xl grid-cols-1 items-center gap-6 px-4 pb-10 pt-20 sm:gap-8 sm:px-6 sm:pb-14 sm:pt-20 lg:min-h-[560px] lg:grid-cols-2 lg:gap-12 lg:px-8 lg:pb-12 lg:pt-24">
            <motion.div
              className="max-w-xl"
              initial="hidden"
              animate="visible"
              variants={staggerContainer}
            >
              <motion.span
                className="mb-5 inline-block text-sm uppercase tracking-[0.2em] text-[#A24C2B]"
                variants={fadeInUp}
              >
                Premium Small-Batch Coffee · South Africa
              </motion.span>

              <motion.h1
                className="mb-5 font-heading text-4xl leading-[1.05] text-[#f5eee7] sm:text-5xl lg:text-6xl"
                variants={fadeInUp}
              >
                South African Landscapes
                <br />
                <span className="text-[#B9532C]">
                  in Every Cup
                </span>
              </motion.h1>

              <motion.p
                className="mb-6 max-w-lg text-base leading-relaxed text-[#d8cab7] sm:mb-8 sm:text-lg"
                variants={fadeInUp}
              >
                Premium coffee crafted with trusted roasting partners and inspired by the landscapes we love — from fynbos-covered mountains to coastal roads, Karoo horizons, and ember-lit evenings.
              </motion.p>

              <motion.div
                className="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:gap-4"
                variants={fadeInUp}
              >
                <Link
                  to="/products/landscape-bundle"
                  className="btn-primary group inline-flex w-full items-center justify-center gap-2 !px-5 !py-3 text-xs sm:w-auto sm:text-sm"
                  data-testid="hero-shop-btn"
                >
                  Discover the Landscape Bundle
                  <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
                </Link>
                <Link
                  to="/shop"
                  className="btn-secondary group inline-flex w-full items-center justify-center gap-2 !px-5 !py-3 text-xs sm:w-auto sm:text-sm"
                  data-testid="hero-collection-btn"
                >
                  Explore Individual Blends
                  <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
                </Link>
              </motion.div>
            </motion.div>

            <div className="flex w-full flex-col items-center justify-center">
              <div className="relative aspect-square w-full max-w-[260px] rounded-md border border-[#d6b88f1a] bg-[#171210] p-2 shadow-[0_18px_50px_rgba(10,7,5,0.42)] sm:max-w-[360px] lg:max-w-[400px]">
                <div className="relative h-full w-full overflow-hidden rounded-sm bg-[#120d0b]">
                  <AnimatePresence initial={false}>
                    <motion.img
                      key={heroImageIndex}
                      src={HERO_IMAGES[heroImageIndex].url}
                      alt={HERO_IMAGES[heroImageIndex].alt}
                      className="absolute inset-0 h-full w-full object-contain"
                      style={{ filter: 'saturate(0.92) contrast(0.98) brightness(1.01)' }}
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      transition={{ duration: 0.8, ease: 'easeInOut' }}
                      loading={heroImageIndex === 0 ? 'eager' : 'lazy'}
                      fetchPriority={heroImageIndex === 0 ? 'high' : 'auto'}
                      decoding="async"
                    />
                  </AnimatePresence>
                </div>
              </div>

              <div className="mt-5 flex min-h-3 items-center justify-center gap-2" aria-label="Hero slides">
                {HERO_IMAGES.map((_, i) => (
                  <button
                    key={i}
                    onClick={() => setHeroImageIndex(i)}
                    aria-label={`Show hero slide ${i + 1}`}
                    aria-pressed={i === heroImageIndex}
                    className={`h-2 rounded-full transition-all duration-300 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#B9532C] ${
                      i === heroImageIndex
                        ? 'w-6 bg-[#B9532C]'
                        : 'w-2 bg-[#CFC5BC] hover:bg-[#A99A8D]'
                    }`}
                  />
                ))}
              </div>
            </div>
          </div>
        </section>

        <section className="bg-[#0B0705] py-6" data-testid="features-strip">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-6 lg:gap-8">
              {features.map((feature, index) => (
                <div key={index} className="flex items-center gap-3 text-white/90">
                  <feature.icon size={24} weight="light" className="text-[#C86333] flex-shrink-0" />
                  <div>
                    <span className="block text-sm font-medium">{feature.title}</span>
                    <span className="block text-xs text-white/60">{feature.desc}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="section-padding-lg" data-testid="products-section">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              className="text-center mb-16"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <span className="overline block mb-4">Our Collection</span>
              <h2 className="font-heading text-4xl sm:text-5xl text-[#f5eee7] mb-6">
                Exceptional Coffees
              </h2>
              <div className="divider mx-auto" />
            </motion.div>

            {loading ? (
              <div className="flex justify-center py-20">
                <div className="spinner border-[#D05C23] border-t-transparent" />
              </div>
            ) : (
              <motion.div
                className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8"
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                variants={staggerContainer}
              >
                {homepageCardProducts.map((product) => (
                  <motion.div key={product.id} variants={fadeInUp}>
                    <ProductCard product={product} onAuthRequired={() => setAuthModalOpen(true)} />
                  </motion.div>
                ))}
              </motion.div>
            )}

            <motion.div
              className="text-center mt-12"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ delay: 0.4 }}
            >
              <Link
                to="/shop"
                className="inline-flex items-center gap-2 text-[#D05C23] font-medium hover:gap-4 transition-all link-underline"
                data-testid="view-all-products"
              >
                View All Products
                <ArrowRight size={18} />
              </Link>
            </motion.div>
          </div>
        </section>

        <section className="section-padding bg-[#171210]" data-testid="google-reviews-section">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-10">
              <span className="overline block mb-4">Google Reviews</span>
              <h2 className="font-heading text-4xl sm:text-5xl text-[#f5eee7] mb-4">
                Shared by Coffee Lovers
              </h2>
              <p className="text-[#d8cab7] max-w-xl mx-auto">
                A selection of reviews customers have shared on our Google profile.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
              {GOOGLE_REVIEWS.map((review) => (
                <article key={review.author} className="bg-[#120d0b] border border-[#d6b88f1a] p-6 flex flex-col">
                  <div className="flex items-center justify-between gap-4 mb-4">
                    <h3 className="font-medium text-[#f5eee7]">{review.author}</h3>
                    <span className="flex gap-0.5 flex-shrink-0" aria-label="5 out of 5 stars">
                      {[...Array(5)].map((_, index) => (
                        <Star key={index} size={16} weight="fill" className="text-[#FBBC04]" aria-hidden="true" />
                      ))}
                    </span>
                  </div>
                  <blockquote className="text-[#d8cab7] text-sm leading-relaxed flex-1">
                    &ldquo;{review.quote}&rdquo;
                  </blockquote>
                  <a
                    href={review.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="mt-5 text-sm text-[#A94826] hover:underline underline-offset-4"
                  >
                    Read review on Google
                  </a>
                </article>
              ))}
            </div>

            <div className="text-center mt-8">
              <a
                href="https://maps.app.goo.gl/uZbPKRgF8uCBHygHA"
                target="_blank"
                rel="noopener noreferrer"
                className="btn-secondary inline-flex items-center justify-center"
              >
                View Google Business Profile
              </a>
            </div>
          </div>
        </section>

        <section className="section-padding-lg bg-[#120d0b]" data-testid="story-section">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8 }}
              >
                <div className="relative">
                  <div className="aspect-[4/5] overflow-hidden relative group">
                    <motion.img
                      src={STORY_IMAGE.url}
                      alt={STORY_IMAGE.alt}
                      className="w-full h-full object-cover"
                      initial={{ opacity: 0 }}
                      whileInView={{ opacity: 1 }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.7 }}
                      loading="lazy"
                      width={1080}
                      height={1080}
                      decoding="async"
                    />

                    <Link
                      to={STORY_IMAGE.shopPath}
                      className="absolute bottom-4 right-4 px-4 py-2 text-xs tracking-[0.12em] uppercase bg-white/90 text-[#2C1A12] hover:bg-white transition-colors"
                    >
                      Shop Now
                    </Link>
                  </div>
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8 }}
              >
                <span className="overline block mb-4">The Cape Ember Experience</span>
                <h2 className="font-heading text-4xl sm:text-5xl text-[#f5eee7] mb-6">
                  From Landscape<br />to Cup
                </h2>
                <div className="space-y-4 text-[#d8cab7] leading-relaxed mb-8">
                  <p>
                    Cape Ember Coffee Co. was created to bring the feeling of South Africa&apos;s landscapes into the everyday coffee ritual.
                  </p>
                  <p>
                    We work with trusted roasting partners to craft premium coffee experiences inspired by the places we love.
                  </p>
                </div>
                <Link
                  to="/about"
                  className="btn-secondary inline-flex items-center gap-3"
                  data-testid="story-cta"
                >
                  Discover Our Story
                  <ArrowRight size={18} />
                </Link>
              </motion.div>
            </div>
          </div>
        </section>

        {testimonials.length > 0 && (
          <section className="section-padding bg-[#2C1A12]" data-testid="testimonials-section">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <motion.div
                className="text-center mb-12"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
              >
                <span className="text-[#C86333] text-sm tracking-[0.3em] uppercase block mb-4">
                  Customer Reviews
                </span>
                <h2 className="font-heading text-4xl sm:text-5xl text-white mb-4">
                  Loved by Coffee Enthusiasts
                </h2>
                <p className="text-white/60 text-lg max-w-xl mx-auto">
                  Feedback from Cape Ember customers
                </p>
              </motion.div>

              <motion.div
                className="bg-white/5 backdrop-blur-sm border border-white/10 p-8 mb-10 max-w-lg mx-auto"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
              >
                <div className="flex items-center justify-center gap-6">
                  <div className="text-center">
                    <span className="text-5xl font-heading text-white block">{customerReviewStats.rating.toFixed(1)}</span>
                    <div className="flex gap-0.5 justify-center my-2">
                      {[...Array(5)].map((_, i) => (
                        <Star key={i} size={20} weight="fill" className="text-[#FBBC04]" />
                      ))}
                    </div>
                    <span className="text-white/60 text-sm">{customerReviewStats.totalReviews} customer reviews</span>
                  </div>
                  <div className="w-px h-24 bg-white/20" />
                  <div className="space-y-1">
                    {customerReviewStats.distribution.map((d) => (
                      <div key={d.stars} className="flex items-center gap-2 text-xs">
                        <span className="text-white/60 w-3">{d.stars}</span>
                        <div className="w-24 h-2 bg-white/10 rounded-full overflow-hidden">
                          <div
                            className="h-full bg-[#FBBC04] rounded-full"
                            style={{ width: `${d.percentage}%` }}
                          />
                        </div>
                        <span className="text-white/40 w-8">{d.percentage}%</span>
                      </div>
                    ))}
                  </div>
                </div>
              </motion.div>

              <div className="text-center -mt-5 mb-8">
                <a
                  href="https://maps.app.goo.gl/uZbPKRgF8uCBHygHA"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-white/70 hover:text-white underline underline-offset-4"
                >
                  View our Google Business Profile
                </a>
              </div>

              <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
                {testimonials.map((testimonial, index) => (
                  <motion.div
                    key={index}
                    className="bg-white/5 backdrop-blur-sm border border-white/10 p-6"
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.1 }}
                  >
                    <div className="flex items-center gap-3 mb-4">
                      <div className="w-10 h-10 rounded-full bg-[#D05C23] flex items-center justify-center text-white font-medium">
                        {(testimonial.user_name || 'C').charAt(0)}
                      </div>
                      <div>
                        <span className="block text-white font-medium text-sm">{testimonial.user_name}</span>
                        <span className="text-white/50 text-xs">Customer Review</span>
                      </div>
                      {testimonial.is_verified_purchase && (
                        <span className="ml-auto flex items-center gap-1 text-[#2F855A] text-xs">
                          <ShieldCheck size={14} weight="fill" />
                          Verified
                        </span>
                      )}
                    </div>
                    <div className="flex gap-0.5 mb-3">
                      {[...Array(Number(testimonial.rating || 0))].map((_, i) => (
                        <Star key={i} size={14} weight="fill" className="text-[#FBBC04]" />
                      ))}
                    </div>
                    <p className="text-white/80 text-sm leading-relaxed">
                      &ldquo;{testimonial.content}&rdquo;
                    </p>
                  </motion.div>
                ))}
              </div>
            </div>
          </section>
        )}

        <section className="section-padding" data-testid="brewing-methods-section">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              className="text-center mb-12"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <span className="overline block mb-4">How to Brew</span>
              <h2 className="font-heading text-4xl sm:text-5xl text-[#f5eee7] mb-4">
                Your Perfect Cup Awaits
              </h2>
              <p className="text-[#d8cab7] max-w-xl mx-auto">
                Our coffees are crafted to shine with any brewing method. Here are our favorites.
              </p>
            </motion.div>

            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
              {brewingMethods.map((method, index) => (
                <motion.div
                  key={method.name}
                  className="text-center p-6 bg-[#171210] border border-[#d6b88f1a] hover:bg-[#1d1715] transition-colors"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.05 }}
                >
                  <span className="text-3xl mb-3 block">{method.icon}</span>
                  <span className="font-medium text-[#f5eee7] text-sm block">{method.name}</span>
                  <span className="text-xs text-[#d8cab7]">{method.time}</span>
                </motion.div>
              ))}
            </div>

            <motion.div
              className="text-center mt-8"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
            >
              <Link
                to="/brew-guide"
                className="inline-flex items-center gap-2 text-[#D05C23] font-medium hover:gap-4 transition-all link-underline"
              >
                View Full Brew Guide
                <ArrowRight size={18} />
              </Link>
            </motion.div>
          </div>
        </section>

        <section className="py-12 border-t border-[#E6DCD1]" data-testid="trust-section">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
              {[
                { label: 'Secure Checkout', icon: ShieldCheck, desc: 'SSL Encrypted' },
                { label: 'Small-Batch Quality', icon: Medal, desc: 'Artisan Crafted' },
                { label: 'Proudly South African', icon: Fire, desc: 'Local Excellence' },
                { label: 'Nationwide Delivery', icon: Truck, desc: 'Complimentary Over R399' },
              ].map((badge, index) => (
                <motion.div
                  key={index}
                  className="flex flex-col items-center gap-2"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                >
                  <div className="w-12 h-12 rounded-full bg-[#F4EFE6] flex items-center justify-center">
                    <badge.icon size={24} weight="light" className="text-[#D05C23]" />
                  </div>
                  <span className="text-[#2C1A12] text-sm font-medium">{badge.label}</span>
                  <span className="text-[#6B5048] text-xs">{badge.desc}</span>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        <AuthModal
          isOpen={authModalOpen}
          onClose={() => setAuthModalOpen(false)}
          initialMode="login"
        />
      </div>
    );
  };

  export default HomePage;
